import { v4 } from "uuid";
import { ROUTES } from "../../constant";
import { SELECTED_CHAT_DATA } from "../../constant/dummyData";
import { ScreenTypes } from "../../enums";
import { iChatData } from "../../types";
import { rotateChatId } from "../../utils/chat/rotateChatId";
import { joinFragments } from "../../services/voice/liveTranscript";
import type { VoiceSession } from "./types";
import { VoiceRuntime, voiceLog, isRequestRunning } from "./runtime";
import { withVoiceReply, voiceUserEntry, withCleanPrompts, withMessage, bubbleText } from "./chatEntries";
import type { TranscriptIO } from "./transcriptIO";

export const createBubbles = (rt: VoiceRuntime, transcriptIO: TranscriptIO) => {
  const { live, sessionRef, deferredRef, repliesRef, emptiedRef, openedFromRef } = rt;
  const { adoptChatId, cleanerOf } = transcriptIO;

  const applyTargets = (session: VoiceSession) => {
    const cleaner = cleanerOf(session);
    session.targets = session.targets.filter((target) => {
      const cleaned = cleaner.cleaned(target.fragments);
      const text = target.filed ? cleaner.best(target.fragments) : cleaned.text || cleaner.best(target.fragments);
      const { entryId, sent } = target;
      if (entryId && text !== target.shown) {
        target.shown = text;
        live.current.state.setChatData((prev: iChatData[]) => withMessage(prev, entryId, text));
      }
      if (!cleaned.complete) return true;
      if (sent && sent !== cleaned.text) {
        live.current.state.setChatData((prev: iChatData[]) => withCleanPrompts(prev, sent, cleaned.text));
        target.sent = cleaned.text;
      }
      return !cleaned.final || target.fragments === session.lateLine?.fragments;
    });
  };

  const applyReplies = () => {
    repliesRef.current = repliesRef.current.filter(({ turn, cleaner }) => {
      const cleaned = cleaner.cleaned(turn.words);
      if (!cleaned.complete) return true;
      if (cleaned.text !== turn.reply && live.current.state.chatData.some((entry: iChatData) => entry.id === turn.userEntryId)) {
        const first = turn.reply === null;
        turn.reply = cleaned.text;
        live.current.state.setChatData((prev: iChatData[]) => withVoiceReply(prev, turn, cleaned.text));
        if (first) {
          live.current.history.setCallSaveHistoryAPIFlag({ flag: true, method: "PUT" });
          voiceLog("narration.saved", { traceId: turn.traceId, chars: cleaned.text.length });
        }
      }
      return !cleaned.final;
    });
  };

  const openChat = (): boolean => {
    const { state: current, history: historyCtx, location: where } = live.current;
    if (current.chatData.length > 0) return false;
    openedFromRef.current = { session: sessionRef.current, firstScreen: current.firstScreen, screen: current.screen, path: where.pathname };
    rotateChatId();
    historyCtx.setCurrentHistoryId("");
    current.setSelectedChatData({ ...SELECTED_CHAT_DATA });
    if (current.isMobile) live.current.navigate(ROUTES.TopDestination);
    else current.setFirstScreen(ScreenTypes.topDestination);
    current.setScreen("");
    return true;
  };

  const nothingAhead = (session: VoiceSession): boolean =>
    sessionRef.current === session &&
    !isRequestRunning() &&
    deferredRef.current.length === 0 &&
    session.filings.every((filing) => filing.shown !== null) &&
    session.answers.every((answer) => answer.shown !== null) &&
    !live.current.state.chatData.some((entry: iChatData) => entry.isLoading);

  const showRequestEarly = (session: VoiceSession) => {
    if (session.earlyUserId || session.handOff || isRequestRunning() || deferredRef.current.length > 0 || session.filings.length > 0) return;
    if (!session.transcript.hasRealReply()) return;
    const fragments = [...session.transcript.pendingFragments()];
    const text = bubbleText(cleanerOf(session), fragments);
    if (!text) return;
    const { state: current } = live.current;
    openChat();
    adoptChatId(session.id);
    const entry = voiceUserEntry(text, session.id);
    session.earlyUserId = entry.id;
    session.targets.push({ fragments, entryId: entry.id, sent: "", shown: text, filed: false });
    current.setChatData((prev: iChatData[]) => [...prev, entry]);
    voiceLog("exchange.early", { userChars: text.length });
  };

  const dropEarlyRequest = (session: VoiceSession) => {
    const id = session.earlyUserId;
    if (!id) return;
    session.earlyUserId = null;
    session.targets = session.targets.filter((target) => target.entryId !== id);
    live.current.state.setChatData((prev: iChatData[]) => prev.filter((entry) => entry.id !== id));
  };

  const showLiveReply = (session: VoiceSession) => {
    const { state: current } = live.current;
    if (session.liveReply) {
      applyTargets(session);
      return;
    }
    if (session.handOff || !session.transcript.hasRealReply()) return;
    const orphan = !joinFragments(session.transcript.pendingFragments());
    if (orphan ? !nothingAhead(session) : !session.earlyUserId) return;
    const fragments = session.transcript.replyFragments();
    const message = bubbleText(cleanerOf(session), fragments);
    const id = v4();
    openChat();
    adoptChatId(session.id);
    current.setChatData((prev: iChatData[]) => [
      ...prev,
      {
        id,
        message,
        isUserMessage: false,
        prompt: [],
        isLoading: false,
        destinationList: [],
        singleSelectedDestination: false,
        responseType: "ai_assistant",
        ...(session.id ? { liveSessionId: session.id } : {}),
      },
    ]);
    session.liveReply = { entryId: id, fragments };
    session.targets.push({ fragments, entryId: id, sent: "", shown: message, filed: false });
    voiceLog("exchange.live", { orphan, replyChars: message.length });
  };

  const dropLiveReply = (session: VoiceSession) => {
    const reply = session.liveReply;
    if (!reply) return;
    session.liveReply = null;
    session.targets = session.targets.filter((target) => target.entryId !== reply.entryId);
    emptiedRef.current = session;
    live.current.state.setChatData((prev: iChatData[]) => prev.filter((entry) => entry.id !== reply.entryId));
  };

  return { applyTargets, applyReplies, openChat, nothingAhead, showRequestEarly, dropEarlyRequest, showLiveReply, dropLiveReply };
};

export type Bubbles = ReturnType<typeof createBubbles>;
